import React from 'react';

/*
 * Velaigal section divider — a tiny editorial ornament used between major
 * sections of public pages. Replaces the raw `<hr />` separator with
 * something that signals brand without becoming decoration for its own
 * sake.
 *
 * Construction:
 *   ─────────  ·  vertical ink stroke  ·  ─────────
 *
 * The vertical stroke is a one-pixel ink mark with a saffron pulli dot
 * floating just above it. The two horizontal hairlines are intentional
 * fades that taper out. Reads like a fleuron in a magazine masthead, not
 * a decorative flourish.
 *
 * Use sparingly — once per major page section transition, not between
 * every paragraph.
 */

interface DividerMotifProps {
  className?: string;
  /**
   * Width of each tapering hairline arm. Default 96px gives a 240ish total
   * presence including the central glyph. Use 48 for tighter spaces.
   */
  armWidth?: number;
}

const DividerMotif: React.FC<DividerMotifProps> = ({
  className = '',
  armWidth = 96,
}) => {
  return (
    <div
      role="separator"
      aria-hidden="true"
      className={`flex items-center justify-center select-none pointer-events-none ${className}`}
    >
      {/* Left tapering hairline */}
      <span
        style={{
          display: 'block',
          width: `${armWidth}px`,
          height: '1px',
          background:
            'linear-gradient(to right, transparent, var(--color-hairline-strong))',
        }}
      />
      {/* Glyph cluster */}
      <span
        style={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '4px',
          margin: '0 16px',
        }}
      >
        <span
          style={{
            width: '4px',
            height: '4px',
            borderRadius: '9999px',
            backgroundColor: 'var(--color-saffron)',
          }}
        />
        <span
          style={{
            width: '1px',
            height: '14px',
            backgroundColor: 'var(--color-ink)',
          }}
        />
      </span>
      {/* Right tapering hairline */}
      <span
        style={{
          display: 'block',
          width: `${armWidth}px`,
          height: '1px',
          background:
            'linear-gradient(to left, transparent, var(--color-hairline-strong))',
        }}
      />
    </div>
  );
};

export default DividerMotif;
